import { useEffect, useCallback } from 'react';

interface UseModalOptions {
  isOpen: boolean;
  onClose: () => void;
  preventRouterNavigation?: boolean;
}

/**
 * Centralized modal management hook that handles ESC key events properly
 * and prevents unwanted router navigation
 */
export function useModal({ isOpen, onClose, preventRouterNavigation = true }: UseModalOptions) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        // Stop all propagation to prevent router navigation
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        // Close the modal
        onClose();
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    if (!isOpen) return;

    // Add event listener with capture: true to intercept before other handlers
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [isOpen, handleKeyDown]);

  // Additional effect to prevent browser back navigation on ESC
  useEffect(() => {
    if (!isOpen || !preventRouterNavigation) return;

    // Removed unused handleBeforeUnload function

    const handlePopState = (event: PopStateEvent) => {
      if (isOpen) {
        // If modal is open and user tries to navigate back, close modal instead
        event.preventDefault();
        onClose();
        // Push the current state back to prevent actual navigation
        window.history.pushState(null, '', window.location.href);
      }
    };

    // Add a state to history stack to catch back button
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose, preventRouterNavigation]);

  return {
    // Return modal props that ensure proper event handling
    modalProps: {
      onEscapeKeyDown: (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        onClose();
      },
      onPointerDownOutside: (event: Event) => {
        // Optional: prevent closing on outside click if needed
        event.preventDefault();
      },
    },
  };
}
